/**
 * 紙の安全書類（ヒヤリハット報告書・点検表・作業日報・メモ）の画像を
 * Edge Function `genbasafeai_ocr` に送り、OCR＋匿名化＋4M分析まで済んだ結果を受け取る。
 *
 * 位置づけ: 「紙から取り込み」は集客ではなく**活性化**の機能。
 * 登録直後の空のダッシュボードを、手元の紙を数枚上げるだけで埋められるようにする。
 *
 * 注意: 結果は必ず管理者が確認して確定する（human-in-the-loop）。
 * この関数は「ドラフトを作る」ところまでしか行わず、DB保存はしない。
 */
import imageCompression from 'browser-image-compression';
import { getSupabase, extractFunctionError } from './supabase';
import { AIAnalysisResult, RiskLevel, Category } from '../types';
import { logInfo, logError } from './clientLogger';
import { logger } from '../utils/logger';

/** 1枚あたりの送信サイズ目安。Edge Function 側は約6MBで拒否する。 */
const COMPRESS_OPTIONS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 2200, // 手書きを読むため解像度は落としすぎない
  useWebWorker: true,
  fileType: 'image/jpeg' as const,
};

export interface OcrImportResult {
  success: true;
  /** 画像から読み取った生テキスト（匿名化前）。管理者が原文と見比べるために使う。 */
  ocrText: string;
  documentType: string;
  analysis: AIAnalysisResult;
  usage?: { used: number; limit: number };
}

export interface OcrImportFailure {
  success: false;
  /** 画面にそのまま出せる日本語メッセージ */
  message: string;
  /** 安全書類として読めなかった場合、読めた文字だけ返ることがある */
  ocrText?: string;
  /** 月次上限に達した場合 true（UIでアップグレード案内を出す） */
  limitExceeded?: boolean;
  /** 通信断など、同じ画像でやり直せば成功しうる失敗。UIに再試行ボタンを出す */
  retryable?: boolean;
}

export type OcrImportOutcome = OcrImportResult | OcrImportFailure;

/** Edge Function の生レスポンス（success:false も200で返る） */
interface OcrEdgeResponse {
  success?: boolean;
  ocrText?: string;
  documentType?: string;
  reason?: string;
  error?: string;
  analysis?: Partial<AIAnalysisResult>;
  usage?: { used: number; limit: number };
}

/** 通信断のときだけ自動で送り直す回数と待ち時間 */
const NETWORK_RETRIES = 2;
const RETRY_DELAYS_MS = [1200, 3000];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * リクエストがサーバーに届かなかった種類の失敗か。
 * supabase-js は fetch 失敗を FunctionsFetchError('Failed to fetch') に包む。
 */
function isNetworkFailure(error: unknown): boolean {
  const name = (error as { name?: string })?.name ?? '';
  const msg = ((error as { message?: string })?.message ?? '').toLowerCase();
  return name === 'FunctionsFetchError'
    || msg.includes('failed to fetch')
    || msg.includes('networkerror')
    || msg.includes('load failed');
}

function toBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      // "data:image/jpeg;base64,XXXX" の XXXX 部分だけを渡す
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * 画像1枚を取り込む。失敗時も例外を投げず OcrImportFailure を返す
 * （複数枚を順に処理するので、1枚の失敗で全体を止めない）。
 */
export async function importFromImage(
  file: File,
  organizationId: string
): Promise<OcrImportOutcome> {
  const supabase = getSupabase();
  if (!supabase) {
    return { success: false, message: 'サービスに接続できません。時間をおいて再度お試しください。' };
  }
  if (!organizationId) {
    return { success: false, message: '組織情報が取得できませんでした。再ログインしてお試しください。' };
  }

  try {
    // 送信前に圧縮（スマホ写真は数MB〜十数MBある）
    let payload: Blob = file;
    let mimeType = file.type || 'image/jpeg';
    try {
      payload = await imageCompression(file, COMPRESS_OPTIONS);
      mimeType = 'image/jpeg';
    } catch (e) {
      // 圧縮に失敗しても、元ファイルが小さければそのまま送る
      logger.warn('importFromImage: compression failed, sending original', e);
      if (file.size > 6 * 1024 * 1024) {
        return { success: false, message: '画像サイズが大きすぎます。撮影サイズを小さくしてお試しください。' };
      }
    }

    const imageBase64 = await toBase64(payload);
    logInfo('OCR import: invoking edge function', { name: file.name, bytes: payload.size }, 'ocrService');

    // 1枚のOCRは10秒前後かかるため、その間に回線が切れることがある
    // （実際に HTTP/3 の ERR_QUIC_PROTOCOL_ERROR で1ページだけ落ちた）。
    // 通信断はサーバーに届いていない＝画像の問題ではないので、圧縮をやり直さず
    // 送信だけを自動で再試行する。上限超過やサーバーエラーは再試行しない。
    let data: OcrEdgeResponse | null = null;
    let raw = '';
    let serverMessage: string | null = null;
    let lastNetworkError = false;

    for (let attempt = 0; attempt < NETWORK_RETRIES + 1; attempt++) {
      if (attempt > 0) {
        await sleep(RETRY_DELAYS_MS[attempt - 1]);
        logInfo('OCR import: retrying after network error', { attempt, name: file.name }, 'ocrService');
      }

      const res = await supabase.functions.invoke('genbasafeai_ocr', {
        body: { imageBase64, mimeType, organizationId },
      });

      if (!res.error) { data = res.data as OcrEdgeResponse; break; }

      /* context は Response。以前は .body を文字列として読んでいたが、それは
         常に空振りし LIMIT_EXCEEDED を取りこぼしていた（上限に達しても
         「撮り直してください」と案内していた）。 */
      const ex = await extractFunctionError(res.error);
      raw = [ex.code, ex.message].filter(Boolean).join(' ');
      serverMessage = ex.message;

      // 応答が届いていない = リクエストがサーバーに達していない
      lastNetworkError = !ex.reached && isNetworkFailure(res.error);
      logError('OCR import failed', {
        message: res.error.message, raw: raw.slice(0, 300), attempt, network: lastNetworkError,
      }, 'ocrService');

      if (!lastNetworkError) break;
    }

    if (!data) {
      if (raw.includes('LIMIT_EXCEEDED')) {
        return {
          success: false,
          limitExceeded: true,
          message: '今月の取り込み上限（ページ数）に達しました。「設定・プラン」からプランを変更すると続けて取り込めます。',
        };
      }
      if (lastNetworkError) {
        return {
          success: false,
          retryable: true,
          message: '通信が途切れたため送信できませんでした。画像の問題ではありません。「再試行」を押してください。',
        };
      }
      return {
        success: false,
        retryable: true,
        /* サーバーが理由を返しているならそれを見せる。撮り直しの案内は
           理由が分からないときだけにする。 */
        message: serverMessage
          ?? '読み取りに失敗しました。明るい場所で撮り直すか、別の画像でお試しください。',
      };
    }

    // Edge Function は 200 でも success:false（安全書類でない等）を返す
    if (data.success !== true) {
      return {
        success: false,
        message: String(data.reason || data.error || '安全に関する書類として読み取れませんでした。'),
        ocrText: typeof data.ocrText === 'string' ? data.ocrText : undefined,
      };
    }

    const a = data.analysis || {};
    return {
      success: true,
      ocrText: String(data.ocrText || ''),
      documentType: String(data.documentType || ''),
      usage: data.usage,
      analysis: {
        title: String(a.title || '紙から取り込んだ報告'),
        formattedBody: String(a.formattedBody || ''),
        riskLevel: (a.riskLevel as RiskLevel) || RiskLevel.MEDIUM,
        category1: (a.category1 as Category) || Category.METHOD,
        category2: String(a.category2 || ''),
        lesson: String(a.lesson || ''),
      },
    };
  } catch (e) {
    logError('OCR import unexpected error', { message: e instanceof Error ? e.message : String(e) }, 'ocrService');
    return { success: false, retryable: true, message: '処理中にエラーが発生しました。「再試行」を押してください。' };
  }
}
